import { ComponentProps, FC } from 'react';

import { Link } from '@inertiajs/react';
import { CheckCircle, Sparkles } from 'lucide-react';

import { cn, getSalesCountText } from '../../utils';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/Tooltip';

interface Props {
    readonly sellerName?: string | null;
    readonly sellerNickname?: string | null;
    readonly sellerPath?: string | null;
    readonly sellerAvatar?: string | null;
    readonly completedSalesCount?: number | null;
    readonly premiumActive?: boolean;
    readonly showPremiumIcon?: boolean;
    readonly label?: string;
    readonly labelClassName?: string;
    readonly size?: 'compact' | 'default';
    readonly className?: string;
    readonly contentClassName?: string;
    readonly onSellerClick?: ComponentProps<typeof Link>['onClick'];
}

const normalizeText = (value?: string | null) =>
    typeof value === 'string' ? value.trim() : '';

export const OfferSellerSummary: FC<Props> = ({
    sellerName,
    sellerNickname,
    sellerPath,
    sellerAvatar,
    completedSalesCount,
    premiumActive = false,
    showPremiumIcon = false,
    label,
    labelClassName,
    size = 'compact',
    className,
    contentClassName,
    onSellerClick,
}) => {
    const normalizedSellerName = normalizeText(sellerName);
    const normalizedSellerNickname = normalizeText(sellerNickname);
    const normalizedSellerPath = normalizeText(sellerPath);
    const fallbackSlug =
        normalizedSellerPath.split('/').filter(Boolean).at(-1) || '';
    const displayName =
        normalizedSellerNickname ||
        normalizedSellerName ||
        fallbackSlug ||
        'Продавець';
    const salesCount = completedSalesCount ?? 0;
    const href = normalizedSellerPath || '#';
    const avatarSrc = normalizeText(sellerAvatar) || undefined;

    return (
        <div
            className={cn(
                'flex min-w-0 items-center',
                size === 'default' ? 'gap-3' : 'gap-2',
                label && 'justify-between',
                className,
            )}
        >
            {label && (
                <span
                    className={cn(
                        'shrink-0 text-xs text-muted-foreground',
                        labelClassName,
                    )}
                >
                    {label}
                </span>
            )}

            <div
                className={cn(
                    'flex min-w-0 items-center',
                    size === 'default' ? 'gap-3' : 'gap-2',
                    label ? 'ml-auto justify-end' : 'justify-start',
                    contentClassName,
                )}
            >
                <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                        <Badge
                            variant="outline"
                            className={cn(
                                'flex shrink-0 cursor-help items-center px-1.5',
                                size === 'default'
                                    ? 'gap-1 py-0.5 text-xs'
                                    : 'h-5 gap-0.5 py-0 text-[10px]',
                            )}
                        >
                            <CheckCircle
                                className={cn(
                                    'text-green-600',
                                    size === 'default'
                                        ? 'h-3 w-3'
                                        : 'h-2.5 w-2.5',
                                )}
                            />
                            <span className="font-medium">{salesCount}</span>
                        </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="top" align="center">
                        <p>{getSalesCountText(salesCount)}</p>
                    </TooltipContent>
                </Tooltip>

                <div
                    className={cn(
                        'flex min-w-0 items-center',
                        size === 'default' ? 'gap-3' : 'gap-2',
                    )}
                >
                    <Avatar
                        className={cn(
                            'shrink-0',
                            size === 'default' ? 'h-6 w-6' : 'h-5 w-5',
                        )}
                    >
                        <AvatarImage src={avatarSrc} />
                        <AvatarFallback
                            className={cn(size === 'compact' && 'text-[10px]')}
                        >
                            {displayName.charAt(0)}
                        </AvatarFallback>
                    </Avatar>

                    <div className="flex min-w-0 items-center gap-1.5">
                        <Link
                            href={href}
                            className="min-w-0"
                            onClick={onSellerClick}
                        >
                            <span
                                className={cn(
                                    'block truncate font-medium underline',
                                    size === 'default' ? 'text-sm' : 'text-xs',
                                )}
                            >
                                {displayName}
                            </span>
                        </Link>

                        {showPremiumIcon && premiumActive && (
                            <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                    <Badge
                                        variant="warning"
                                        className={cn(
                                            'shrink-0 cursor-help justify-center p-0',
                                            size === 'default'
                                                ? 'h-5 w-5'
                                                : 'h-4 w-4',
                                        )}
                                        aria-label="Premium пропозиція"
                                    >
                                        <Sparkles
                                            className={cn(
                                                size === 'default'
                                                    ? 'h-3 w-3'
                                                    : 'h-2.5 w-2.5',
                                            )}
                                        />
                                    </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                    Premium пропозиція
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
